import React from 'react'
import { notNull } from '@gap/utils/utils'
import GapDataGridStore from '../GapDataGridStore'
import { makeAutoObservable } from 'mobx'
import ModalState from 'reusable/mobx-helpers/ModalState'
import { GapDataGridAdditionalFilter } from '../GapDataGrid'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import { GridLogicOperator, GridValidRowModel } from '@mui/x-data-grid'

interface GapDataGridToolbarStoreProps<T extends GridValidRowModel> {
   gapDataGridStore: GapDataGridStore<T>
}

export default class GapDataGridToolbarStore<T extends GridValidRowModel> {
   sp
   search = ''
   tab = 0
   filterState = new ModalState()
   selectedFilter: GapDataGridAdditionalFilter<T> | undefined = undefined

   constructor(sp: GapDataGridToolbarStoreProps<T>) {
      this.sp = sp
      makeAutoObservable(this)
   }

   get toolbar() {
      return this.sp.gapDataGridStore.sp.toolbar
   }

   get apiRef() {
      return this.sp.gapDataGridStore.sp.gridApiRef
   }

   get defaultAdditionalFilter() {
      return this.sp.gapDataGridStore.sp.toolbar?.additionalFilters?.find(f => f.default)
   }

   /**
    * If this is supplied, try and get the right button props for the tab. If no 'onlyForTab' exists, than we assume no filter for the selectionButton
    * Otherwise try to get the right props based on what tab we are on.
    * Ex. A button will show on the open status tab saying 'Archive 5 clients'. This defines those props
    */
   get selectionButton() {
      if (!this.toolbar?.onSelection) {
         return undefined
      }

      const correctSelectionButton = this.toolbar.onSelection.find(selection => {
         if (selection.onlyForArchiveMode !== undefined) {
            return selection.onlyForArchiveMode === this.sp.gapDataGridStore.archiveMode
         }

         if (!selection.onlyForTab) {
            return selection
         }

         const currentTabName = notNull(this.sp.gapDataGridStore.tabs?.find((_, i) => i === this.tab), 'no tab found for current index').label
         return selection.onlyForTab === currentTabName
      })

      const count = this.sp.gapDataGridStore.selectedCount

      if (count === 0 || !correctSelectionButton) {
         return undefined
      }

      return {
         label: correctSelectionButton.buttonLabel(count),
         onClick: () => correctSelectionButton.callback(this.sp.gapDataGridStore.selectedIds)
      }
   }

   get columnsThatNeedHiding() {
      const tab = this.sp.gapDataGridStore.tabs?.[this.tab]

      return this.sp.gapDataGridStore.columns.filter(x => {
         if (x.hide) return true
         if (!x.tabFilters?.length) return false
         if (!tab) return false
         return !x.tabFilters.some(x => tab.label.startsWith(x))
      }).map(x => x.field).reduce<Record<string, boolean>>(
         (acc, field) => {
            acc[field] = false
            return acc
         }, {}
      )
   }

   setFilterModel = () => {
      const combinedItems: any[] = []

      // Add tab filters if tabs exist
      if (this.sp.gapDataGridStore.tabs?.length) {
         const { items, noRowsText } = notNull(this.sp.gapDataGridStore.tabs)[this.tab]
         this.sp.gapDataGridStore.noRowsTextForTab = noRowsText
         combinedItems.push(...items)
      }

      // When tabs are configured, always replace the full filter model so that switching
      // to a tab with no filters (e.g. "All") clears items set by a previous tab.
      // Otherwise (no tabs at all) just update the quick filter to preserve any externally-set items.
      if (this.sp.gapDataGridStore.tabs?.length) {
         this.apiRef.current?.setFilterModel({
            items: combinedItems,
            logicOperator: GridLogicOperator.And,
            quickFilterValues: this.search ? [this.search] : []
         })
      } else {
         this.apiRef.current?.setQuickFilterValues([this.search])
      }

      this.apiRef.current?.setColumnVisibilityModel(this.columnsThatNeedHiding)
   }

   changeTabs = (_: React.SyntheticEvent, newTabIndex: number) => {
      this.tab = newTabIndex
      this.setFilterModel()
   }

   changeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
      this.search = event.target.value
      this.setFilterModel()
   }

   clearSearch = () => {
      this.search = ''
      this.setFilterModel()
   }
}

export const { Provider: GapDataGridToolbarStoreProvider, useProvidedStore: useGapDataGridToolbarStore } = createStoreContext(GapDataGridToolbarStore<any>)
